import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { ChevronDown, Coins, Gift, Ticket, Zap, Target, XCircle, Plus, Trash2 } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import type { IOutcomeRanges, IDenomination } from "@/lib/slotMachineTypes"

interface OutcomeRangesSectionProps {
  ranges: IOutcomeRanges
  onChange: (ranges: IOutcomeRanges) => void
  disabled?: boolean
}

const CASINO_COLORS = {
  bg: "#121220",
  bgLight: "#1a1a2e",
  orange: "#F37020",
  gold: "#FFD700",
  green: "#32CD32",
  red: "#FF4444",
  purple: "#9B59B6",
  blue: "#3498DB",
  border: "rgba(255,255,255,0.1)",
  text: "rgba(255,255,255,0.9)",
  textMuted: "rgba(255,255,255,0.6)",
}

// Range type configs with icons and colors
const RANGE_CONFIG: Record<keyof IOutcomeRanges, {
  icon: React.ComponentType<{ className?: string }>
  color: string
  label: string
  description: string
  inputType: "number" | "text" | "quest"
  inputLabel: string
}> = {
  points: {
    icon: Coins,
    color: CASINO_COLORS.gold,
    label: "Points",
    description: "Point denominations (1-100)",
    inputType: "number",
    inputLabel: "Point Value",
  },
  bonus: {
    icon: ({ className }) => <span className={className}>$</span>,
    color: CASINO_COLORS.green,
    label: "Bonus",
    description: "Dollar bonus amounts",
    inputType: "number",
    inputLabel: "Dollar Amount",
  },
  merchandise: {
    icon: Gift,
    color: CASINO_COLORS.purple,
    label: "Merchandise",
    description: "Physical items & prizes",
    inputType: "text",
    inputLabel: "Item Name",
  },
  freeSpins: {
    icon: Zap,
    color: CASINO_COLORS.blue,
    label: "Free Spins",
    description: "Extra spin rewards (1-3)",
    inputType: "number",
    inputLabel: "Spin Count",
  },
  quest: {
    icon: Target,
    color: CASINO_COLORS.orange,
    label: "Quest",
    description: "Side quest challenges",
    inputType: "quest",
    inputLabel: "Quest Name",
  },
  miss: {
    icon: XCircle,
    color: CASINO_COLORS.red,
    label: "Miss",
    description: "Bust outcome (no prize)",
    inputType: "text",
    inputLabel: "Message",
  },
}

// Auto-balance denominations within a range
const autoBalanceDenominations = (
  items: IDenomination[],
  changedIndex: number,
  newValue: number
): IDenomination[] => {
  const result = [...items.map(d => ({ ...d }))]
  const oldValue = result[changedIndex].probability
  const delta = newValue - oldValue
  
  const others = result.filter((_, i) => i !== changedIndex)
  const otherTotal = others.reduce((sum, d) => sum + d.probability, 0)
  
  if (otherTotal === 0 && delta > 0) return result
  
  const maxValue = Math.min(100, oldValue + otherTotal)
  const clampedValue = Math.max(0, Math.min(maxValue, newValue))
  const actualDelta = clampedValue - oldValue
  
  result[changedIndex].probability = clampedValue
  
  if (actualDelta !== 0 && otherTotal > 0) {
    for (let i = 0; i < result.length; i++) {
      if (i !== changedIndex) {
        const proportion = result[i].probability / otherTotal
        const adjustment = -actualDelta * proportion
        result[i].probability = Math.max(0, Math.round((result[i].probability + adjustment) * 100) / 100)
      }
    }
  }
  
  // Fix rounding errors
  const total = result.reduce((sum, d) => sum + d.probability, 0)
  if (Math.abs(total - 100) > 0.01) {
    const diff = 100 - total
    const largestIdx = result
      .map((d, i) => ({ d, i }))
      .filter(({ i }) => i !== changedIndex)
      .sort((a, b) => b.d.probability - a.d.probability)[0]?.i
    if (largestIdx !== undefined) {
      result[largestIdx].probability = Math.round((result[largestIdx].probability + diff) * 100) / 100
    }
  }
  
  return result
}

// Add denomination and redistribute probability
const addDenomination = (items: IDenomination[], newItem: IDenomination): IDenomination[] => {
  const newProb = newItem.probability || (100 / (items.length + 1))
  const result = items.map(d => ({
    ...d,
    probability: Math.round((d.probability * (100 - newProb) / 100) * 100) / 100
  }))
  result.push({ ...newItem, probability: newProb })
  
  // Fix rounding
  const total = result.reduce((sum, d) => sum + d.probability, 0)
  if (Math.abs(total - 100) > 0.01) {
    result[result.length - 1].probability += (100 - total)
  }
  return result
}

// Remove denomination and redistribute its probability
const removeDenomination = (items: IDenomination[], index: number): IDenomination[] => {
  if (items.length <= 1) return items
  const removedProb = items[index].probability
  const result = items.filter((_, i) => i !== index).map(d => ({ ...d }))
  const otherTotal = result.reduce((sum, d) => sum + d.probability, 0)
  
  if (otherTotal > 0) {
    result.forEach(d => {
      d.probability += (removedProb * (d.probability / otherTotal))
      d.probability = Math.round(d.probability * 100) / 100
    })
  }
  
  // Fix rounding
  const total = result.reduce((sum, d) => sum + d.probability, 0)
  if (Math.abs(total - 100) > 0.01 && result.length > 0) {
    result[0].probability += (100 - total)
  }
  return result
}

// Individual denomination slider with delete button
function DenominationSlider({
  item,
  onChange,
  onDelete,
  disabled,
  accentColor,
  canDelete,
}: {
  item: IDenomination
  onChange: (probability: number) => void
  onDelete?: () => void
  disabled?: boolean
  accentColor: string
  canDelete?: boolean
}) {
  const [isDragging, setIsDragging] = React.useState(false)

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all"
      style={{
        backgroundColor: CASINO_COLORS.bg,
        borderColor: isDragging ? `${accentColor}60` : CASINO_COLORS.border,
      }}
    >
      {/* Label */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-outfit truncate block" style={{ color: CASINO_COLORS.text }}>
          {item.label}
        </span>
        {item.metadata?.rules && (
          <span className="text-xs font-outfit truncate block" style={{ color: CASINO_COLORS.textMuted }}>
            {item.metadata.rules.substring(0, 30)}{item.metadata.rules.length > 30 ? '...' : ''}
          </span>
        )}
      </div>
      
      {/* Probability display */}
      <motion.span
        key={item.probability}
        initial={{ scale: 1.05 }}
        animate={{ scale: 1 }}
        className="text-xs font-mono font-bold px-2 py-0.5 rounded min-w-[48px] text-center"
        style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
      >
        {item.probability.toFixed(1)}%
      </motion.span>
      
      {/* Slider */}
      <div className="relative w-20 sm:w-28 h-5 flex items-center">
        <div className="absolute inset-0 rounded-full h-1.5 top-1/2 -translate-y-1/2" style={{ backgroundColor: CASINO_COLORS.border }} />
        <motion.div
          className="absolute left-0 rounded-full h-1.5 top-1/2 -translate-y-1/2"
          style={{ width: `${item.probability}%`, backgroundColor: accentColor }}
          animate={{ width: `${item.probability}%` }}
          transition={{ duration: 0.1 }}
        />
        <input
          type="range" min="0" max="100" step="0.5"
          value={item.probability}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          disabled={disabled}
          className="relative w-full h-5 appearance-none bg-transparent cursor-pointer disabled:cursor-not-allowed z-10"
        />
      </div>
      
      {/* Delete button */}
      {canDelete && onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="p-1 rounded hover:bg-red-500/20 transition-colors"
          title="Remove denomination"
        >
          <Trash2 className="w-3.5 h-3.5" style={{ color: CASINO_COLORS.red }} />
        </button>
      )}
      
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%;
          background: ${accentColor}; border: 2px solid ${CASINO_COLORS.bg};
          cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        }
        input[type="range"]::-webkit-slider-thumb:hover { transform: scale(1.1); }
        input[type="range"]:disabled::-webkit-slider-thumb { background: ${CASINO_COLORS.textMuted}; cursor: not-allowed; }
        input[type="range"]::-moz-range-thumb {
          width: 14px; height: 14px; border-radius: 50%;
          background: ${accentColor}; border: 2px solid ${CASINO_COLORS.bg}; cursor: pointer;
        }
      `}</style>
    </div>
  )
}

// Add Denomination Modal
function AddDenominationModal({
  open,
  onOpenChange,
  rangeKey,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  rangeKey: keyof IOutcomeRanges
  onAdd: (item: IDenomination) => void
}) {
  const config = RANGE_CONFIG[rangeKey]
  const [value, setValue] = React.useState("")
  const [label, setLabel] = React.useState("")
  const [rules, setRules] = React.useState("")
  
  const handleAdd = () => {
    if (config.inputType === "number") {
      const numValue = parseFloat(value)
      if (isNaN(numValue) || numValue <= 0) return
      onAdd({
        value: numValue,
        probability: 10,
        label: rangeKey === "bonus" ? `$${numValue}` : `${numValue}`,
      })
    } else if (config.inputType === "quest") {
      if (!label.trim()) return
      onAdd({
        value: label.toLowerCase().replace(/\s+/g, '_'),
        probability: 10,
        label: label.trim(),
        metadata: rules.trim() ? { rules: rules.trim() } : undefined,
      })
    } else {
      if (!label.trim()) return
      onAdd({
        value: label.toLowerCase().replace(/\s+/g, '_'),
        probability: 10,
        label: label.trim(),
      })
    }
    setValue("")
    setLabel("")
    setRules("")
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md w-[95vw] rounded-xl"
        style={{ backgroundColor: CASINO_COLORS.bgLight, borderColor: CASINO_COLORS.border }}
      >
        <DialogHeader>
          <DialogTitle className="font-outfit" style={{ color: config.color }}>
            Add {config.label} Denomination
          </DialogTitle>
          <DialogDescription className="font-outfit" style={{ color: CASINO_COLORS.textMuted }}>
            {config.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {config.inputType === "number" ? (
            <div>
              <label className="text-sm font-outfit block mb-2" style={{ color: CASINO_COLORS.text }}>
                {config.inputLabel}
              </label>
              <input
                type="number"
                min="1"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={rangeKey === "points" ? "e.g. 25" : rangeKey === "bonus" ? "e.g. 10" : "e.g. 5"}
                className="w-full px-3 py-2 rounded-lg border font-outfit text-sm"
                style={{
                  backgroundColor: CASINO_COLORS.bg,
                  borderColor: CASINO_COLORS.border,
                  color: CASINO_COLORS.text,
                }}
              />
            </div>
          ) : (
            <>
              <div>
                <label className="text-sm font-outfit block mb-2" style={{ color: CASINO_COLORS.text }}>
                  {config.inputLabel}
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={rangeKey === "merchandise" ? "e.g. Oval Hoodie" : "e.g. Weekly Challenge"}
                  className="w-full px-3 py-2 rounded-lg border font-outfit text-sm"
                  style={{
                    backgroundColor: CASINO_COLORS.bg,
                    borderColor: CASINO_COLORS.border,
                    color: CASINO_COLORS.text,
                  }}
                />
              </div>
              {config.inputType === "quest" && (
                <div>
                  <label className="text-sm font-outfit block mb-2" style={{ color: CASINO_COLORS.text }}>
                    Quest Rules (optional)
                  </label>
                  <textarea
                    value={rules}
                    onChange={(e) => setRules(e.target.value)}
                    placeholder="Describe the quest objectives and rewards..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border font-outfit text-sm resize-none"
                    style={{
                      backgroundColor: CASINO_COLORS.bg,
                      borderColor: CASINO_COLORS.border,
                      color: CASINO_COLORS.text,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
        
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg font-outfit text-sm transition-colors"
            style={{ backgroundColor: CASINO_COLORS.border, color: CASINO_COLORS.text }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            className="px-4 py-2 rounded-lg font-outfit text-sm font-medium transition-colors"
            style={{ backgroundColor: config.color, color: "#000" }}
          >
            Add
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Delete confirmation modal
function DeleteConfirmModal({
  open,
  onOpenChange,
  itemLabel,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  itemLabel: string
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-sm w-[90vw] rounded-xl"
        style={{ backgroundColor: CASINO_COLORS.bgLight, borderColor: CASINO_COLORS.border }}
      >
        <DialogHeader>
          <DialogTitle className="font-outfit" style={{ color: CASINO_COLORS.red }}>
            Remove Denomination?
          </DialogTitle>
          <DialogDescription className="font-outfit" style={{ color: CASINO_COLORS.textMuted }}>
            Remove "{itemLabel}"? Its probability will be redistributed to other items.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg font-outfit text-sm"
            style={{ backgroundColor: CASINO_COLORS.border, color: CASINO_COLORS.text }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { onConfirm(); onOpenChange(false); }}
            className="px-4 py-2 rounded-lg font-outfit text-sm font-medium"
            style={{ backgroundColor: CASINO_COLORS.red, color: "#fff" }}
          >
            Remove
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Collapsible range subsection with add/delete
function RangeSubsection({
  rangeKey,
  items,
  onChange,
  disabled,
}: {
  rangeKey: keyof IOutcomeRanges
  items: IDenomination[]
  onChange: (items: IDenomination[]) => void
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState<{ index: number; label: string } | null>(null)
  
  const config = RANGE_CONFIG[rangeKey]
  const Icon = config.icon
  const total = items.reduce((sum, d) => sum + d.probability, 0)
  const isBalanced = Math.abs(total - 100) < 0.1
  const isMissSection = rangeKey === "miss"

  const handleSliderChange = (index: number, newValue: number) => {
    onChange(autoBalanceDenominations(items, index, newValue))
  }
  
  const handleAdd = (newItem: IDenomination) => {
    onChange(addDenomination(items, newItem))
  }
  
  const handleDelete = (index: number) => {
    onChange(removeDenomination(items, index))
    setDeleteTarget(null)
  }

  return (
    <>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all duration-200 hover:border-opacity-60"
            style={{
              backgroundColor: CASINO_COLORS.bg,
              borderColor: open ? `${config.color}50` : CASINO_COLORS.border,
            }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: `${config.color}20`, color: config.color }}
              >
                <Icon className="w-4 h-4" />
              </div>
              <div className="text-left">
                <span className="text-sm font-outfit font-medium block" style={{ color: CASINO_COLORS.text }}>
                  {config.label}
                </span>
                <span className="text-xs font-outfit" style={{ color: CASINO_COLORS.textMuted }}>
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isMissSection && (
                <span
                  className="text-xs font-mono px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: isBalanced ? `${CASINO_COLORS.green}20` : `${CASINO_COLORS.red}20`,
                    color: isBalanced ? CASINO_COLORS.green : CASINO_COLORS.red,
                  }}
                >
                  {total.toFixed(0)}%
                </span>
              )}
              <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4" style={{ color: CASINO_COLORS.textMuted }} />
              </motion.div>
            </div>
          </button>
        </CollapsibleTrigger>
        <AnimatePresence initial={false}>
          {open && (
            <CollapsibleContent forceMount asChild>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="overflow-hidden"
              >
                <div className="mt-2 rounded-lg p-3 space-y-2" style={{ backgroundColor: `${CASINO_COLORS.bgLight}80` }}>
                  <p className="text-xs font-outfit mb-2" style={{ color: CASINO_COLORS.textMuted }}>
                    {config.description}
                  </p>
                  {isMissSection ? (
                    <div
                      className="px-3 py-3 rounded-lg border text-center"
                      style={{ backgroundColor: CASINO_COLORS.bg, borderColor: CASINO_COLORS.border }}
                    >
                      <span className="text-sm font-outfit" style={{ color: CASINO_COLORS.textMuted }}>
                        {items[0]?.label || "Better luck next time!"}
                      </span>
                    </div>
                  ) : (
                    <>
                      {items.map((item, index) => (
                        <DenominationSlider
                          key={`${rangeKey}-${index}-${item.value}`}
                          item={item}
                          onChange={(prob) => handleSliderChange(index, prob)}
                          onDelete={() => setDeleteTarget({ index, label: item.label })}
                          disabled={disabled}
                          accentColor={config.color}
                          canDelete={items.length > 1}
                        />
                      ))}
                      {/* Add button */}
                      <button
                        type="button"
                        onClick={() => setShowAddModal(true)}
                        disabled={disabled}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed transition-all hover:border-opacity-80 disabled:opacity-50"
                        style={{ borderColor: config.color, color: config.color }}
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-outfit">Add {config.label}</span>
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </CollapsibleContent>
          )}
        </AnimatePresence>
      </Collapsible>
      
      <AddDenominationModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        rangeKey={rangeKey}
        onAdd={handleAdd}
      />
      
      <DeleteConfirmModal
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        itemLabel={deleteTarget?.label || ""}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget.index)}
      />
    </>
  )
}

export function OutcomeRangesSection({
  ranges,
  onChange,
  disabled = false,
}: OutcomeRangesSectionProps) {
  const handleRangeChange = (key: keyof IOutcomeRanges, items: IDenomination[]) => {
    onChange({ ...ranges, [key]: items })
  }

  const rangeKeys: (keyof IOutcomeRanges)[] = ["points", "bonus", "freeSpins", "merchandise", "quest", "miss"]

  return (
    <div className="space-y-2">
      {rangeKeys.map((key) => (
        <RangeSubsection
          key={key}
          rangeKey={key}
          items={ranges[key]}
          onChange={(items) => handleRangeChange(key, items)}
          disabled={disabled}
        />
      ))}
    </div>
  )
}
